/**
 * A slim cross-sell strip shown on every page (mounted in the Layout) when a merchant uses only one
 * vertical: a compact banner pitching the other segment. It is a SUGGESTION — the merchant cannot
 * self-enable, so the button opens a short "talk to your account manager" dialog.
 *
 * DISMISSAL IS SESSION-ONLY. The ✕ hides it via in-memory state, so it stays gone as the merchant
 * moves around the panel, but it is NOT persisted — on their next login the Layout remounts, the
 * state resets, and the tip shows again. (Deliberately not localStorage: we want it to re-surface.)
 *
 * COLOUR + MOTION: the PACE hero gradient (indigo → blue → sky, violet stop removed) as a FIXED
 * background, with two
 * soft shapes drifting slowly across it — the gradient itself does not animate, only the shapes do.
 */
import React, { useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import { keyframes } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';

import { FONT, SIZE, WEIGHT } from '../../styles/foundation/typography';
import { NAVY, SUBTLE } from '../../styles/foundation/colors';

const WHITE = '#FFFFFF';
/* The PACE hero gradient (src/app/components/pace-about/PaceAboutPage.js) — kept static here. */
const PACE_GRADIENT = 'linear-gradient(120deg, #312E81, #4F46E5, #0EA5E9)';
const PACE_ACCENT = '#4F46E5'; // button text on white

/* Only the shapes move — a slow drift, disabled under prefers-reduced-motion. */
const drift = keyframes`
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(12px, -8px); }
`;

const orbBase = {
  position: 'absolute', borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.12)', zIndex: 0,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
};
const contentLayer = { position: 'relative', zIndex: 1 };

const COPY = {
  D2C: {
    name: 'D2C',
    Icon: Inventory2OutlinedIcon,
    title: 'Expand into D2C delivery',
    sub: 'Reach your customers directly — all on your ProShip account.',
    body: 'ProShip handles direct-to-consumer delivery too — forward, reverse and marketplace fulfilment across our courier network. It is not enabled on your account yet.',
    cta: 'Explore D2C',
  },
  PTL: {
    name: 'PTL',
    Icon: LocalShippingOutlinedIcon,
    title: 'Expand into PTL freight',
    sub: 'Move part-truck-load B2B shipments — all on your ProShip account.',
    body: 'ProShip handles part-truck-load B2B freight too — appointment and non-appointment deliveries across our PTL network. It is not enabled on your account yet.',
    cta: 'Explore PTL',
  },
};

export default function VerticalSuggestionBanner({ vertical }) {
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);

  if (!vertical || !COPY[vertical] || dismissed) return null;
  const c = COPY[vertical];
  const Icon = c.Icon;

  return (
    <>
      <Box
        sx={{
          position: 'relative', overflow: 'hidden', borderRadius: '10px',
          p: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px',
          background: PACE_GRADIENT,
          boxShadow: '0 4px 16px rgba(79,70,229,0.28)',
        }}
      >
        {/* Drifting shapes (the only moving part). */}
        <Box aria-hidden='true' sx={{ ...orbBase, width: 70, height: 70, top: -28, right: 60, animation: `${drift} 7s ease-in-out infinite` }} />
        <Box aria-hidden='true' sx={{ ...orbBase, width: 50, height: 50, bottom: -26, right: 150, animation: `${drift} 9s ease-in-out infinite reverse` }} />

        <Box sx={{ ...contentLayer, width: 30, height: 30, borderRadius: '8px', flexShrink: 0, bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon sx={{ color: WHITE, fontSize: 18 }} />
        </Box>

        <Box sx={{ ...contentLayer, flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], fontWeight: WEIGHT.semibold, color: WHITE, lineHeight: 1.3 }}>
            {c.title}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], color: 'rgba(255,255,255,0.9)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {c.sub}
          </Typography>
        </Box>

        <Button
          size='small'
          onClick={() => setOpen(true)}
          sx={{
            ...contentLayer,
            fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, textTransform: 'none',
            bgcolor: WHITE, color: PACE_ACCENT, whiteSpace: 'nowrap', px: 1.5, py: 0.4, minWidth: 0, borderRadius: '7px',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
          }}
        >
          {c.cta}
        </Button>

        <IconButton size='small' aria-label='Dismiss suggestion' onClick={() => setDismissed(true)} sx={{ ...contentLayer, color: 'rgba(255,255,255,0.8)', p: 0.4 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>
          {c.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.6 }}>
            {c.body}
          </Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY, mt: 1.5 }}>
            To enable {c.name} on your account, reach out to your ProShip account manager.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size='small' onClick={() => setOpen(false)} sx={{ fontFamily: FONT, textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
